import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Chip, FormControlLabel, IconButton, InputAdornment, MenuItem, Paper, Stack, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import {
    EditorProvider, Editor as SimpleWysiwygEditor, Toolbar,
    BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough, BtnLink, BtnNumberedList, BtnBulletList, BtnUndo, BtnRedo, Separator,
} from 'react-simple-wysiwyg';
import { ConfigCard } from '../style/cards';

const LANGS = [
    { code: 'nl', label: 'NL', long: 'Dutch'   },
    { code: 'en', label: 'GB', long: 'English' },
    { code: 'fr', label: 'FR', long: 'French'  },
];

// All chip-able product fields the storefront knows about. Title and
// subtitle pull from this same pool; whatever's left over shows up as
// "Available fields" the admin can click to add.
const CHIP_FIELDS = [
    { key: 'type',      label: 'Type'      },
    { key: 'brand',     label: 'Brand'     },
    { key: 'name',      label: 'Name'      },
    { key: 'reference', label: 'Reference' },
    { key: 'color',     label: 'Color'     },
    { key: 'season',    label: 'Season'    },
    { key: 'subtype',   label: 'Subtype'   },
    { key: 'material',  label: 'Material'  },
];

const ShopTheLookTab = ({ theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const [langTab, setLangTab] = useState(0);
    const lang = LANGS[langTab].code;

    const [config, setConfig] = useState({
        introText: { nl: '', en: '', fr: '' },
        introEditor: 'visual',  // 'visual' | 'html'
        looksGrid: {
            navigationCategory: 'gender',
            displayOrder: 'default',
            looksPerRow: 3,
            showInIframe: false,
        },
        overlay: {
            onlyOnHover: false,
            bandBg: '#ec9a9a',
            bandHeightPct: 23,
            bandVerticalPosition: 'middle',
            showTitle: true,
            titleColor: '#5a1a93',
            titleFontSize: 12,
            showCta: true,
            ctaText: 'Bewonder',
            ctaBg: '#ffffff',
            ctaTextColor: '#5a1a93',
            ctaFontSize: 26,
        },
        detailPage: {
            descriptionPosition: 'below',  // 'below' | 'top'
            showDividerBetweenProducts: true,
            showOutOfStock: true,
        },
        productTitle:    ['type', 'brand'],   // chip order = display order
        productSubtitle: [],                  // empty by default
    });

    const setIntroText = (val) => setConfig(prev => ({
        ...prev,
        introText: { ...prev.introText, [lang]: val },
    }));
    const setSection = (sec) => (patch) => setConfig(prev => ({
        ...prev,
        [sec]: { ...prev[sec], ...patch },
    }));
    const setOverlay  = setSection('overlay');
    const setLooks    = setSection('looksGrid');
    const setDetail   = setSection('detailPage');

    const copyIntroFrom = (fromLang) => {
        if (fromLang === lang) return;
        setConfig(prev => ({
            ...prev,
            introText: { ...prev.introText, [lang]: prev.introText[fromLang] || '' },
        }));
    };

    // Chip helpers: title and subtitle are arrays of field-keys; fields
    // appear in the "available" row when they're in neither.
    const used = new Set([...config.productTitle, ...config.productSubtitle]);
    const availableFields = CHIP_FIELDS.filter(f => !used.has(f.key));
    const addToTitle    = (key) => setConfig(prev => ({ ...prev, productTitle:    [...prev.productTitle,    key] }));
    const addToSubtitle = (key) => setConfig(prev => ({ ...prev, productSubtitle: [...prev.productSubtitle, key] }));
    const removeFrom    = (which) => (key) => setConfig(prev => ({ ...prev, [which]: prev[which].filter(k => k !== key) }));

    return (
        <Stack spacing={2}>
            {/* ─── Intro text ─── */}
            <ConfigCard
                title={tr('cfg_stl_intro_title', 'Intro text')}
                description={tr('cfg_stl_intro_desc', 'A welcome message shown above the looks grid. Each language has its own version.')}
                headerActions={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {tr('cfg_stl_intro_copy_from', 'Copy layout from:')}
                        </Typography>
                        {LANGS.filter((_, i) => i !== langTab).map((l) => (
                            <Button key={l.code} size="small" variant="outlined" onClick={() => copyIntroFrom(l.code)} sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}>
                                {l.long}
                            </Button>
                        ))}
                    </Box>
                }
                theme={theme}
            >
                <Tabs
                    value={langTab}
                    onChange={(_e, v) => setLangTab(v)}
                    sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, px: 1.5, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
                >
                    {LANGS.map((l) => (
                        <Tab key={l.code} label={<Box component="span"><Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>{l.label}</Box>{l.long}</Box>} />
                    ))}
                </Tabs>

                {config.introEditor === 'visual' ? (
                    <Box sx={{
                        border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
                        '& .rsw-editor': { minHeight: 180 },
                        '& .rsw-ce':     { minHeight: 140, padding: '12px 16px' },
                    }}>
                        <EditorProvider>
                            <SimpleWysiwygEditor
                                value={config.introText[lang] || ''}
                                onChange={(e) => setIntroText(e.target.value)}
                            >
                                <Toolbar>
                                    <BtnUndo /><BtnRedo /><Separator />
                                    <BtnBold /><BtnItalic /><BtnUnderline /><BtnStrikeThrough /><Separator />
                                    <BtnNumberedList /><BtnBulletList /><Separator />
                                    <BtnLink />
                                </Toolbar>
                            </SimpleWysiwygEditor>
                        </EditorProvider>
                    </Box>
                ) : (
                    <TextField
                        multiline minRows={6}
                        value={config.introText[lang] || ''}
                        onChange={(e) => setIntroText(e.target.value)}
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                        placeholder="<p>Laat je inspireren door onze herfst- &amp; winterlooks!</p>"
                    />
                )}

                {/* Editor mode toggle — two big buttons across the bottom */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Button
                        size="small"
                        variant={config.introEditor === 'visual' ? 'contained' : 'outlined'}
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setConfig(prev => ({ ...prev, introEditor: 'visual' }))}
                        sx={{ textTransform: 'none', py: 1 }}
                    >
                        {tr('cfg_stl_intro_visual', 'Visual editor')}
                    </Button>
                    <Button
                        size="small"
                        variant={config.introEditor === 'html' ? 'contained' : 'outlined'}
                        startIcon={<CodeIcon fontSize="small" />}
                        onClick={() => setConfig(prev => ({ ...prev, introEditor: 'html' }))}
                        sx={{ textTransform: 'none', py: 1 }}
                    >
                        {tr('cfg_stl_intro_html', 'HTML editor')}
                    </Button>
                </Box>
            </ConfigCard>

            {/* ─── Looks grid ─── */}
            <ConfigCard title={tr('cfg_stl_grid_title', 'Looks grid')} description={tr('cfg_stl_grid_desc', 'How the looks are arranged on the overview page.')} theme={theme}>
                <SettingRow
                    label={tr('cfg_stl_grid_nav_category', 'Category used for navigation')}
                    help={tr('cfg_stl_grid_nav_category_help', 'When visitors filter looks, this is the category tree used for navigation')}
                    control={
                        <TextField
                            select size="small"
                            value={config.looksGrid.navigationCategory}
                            onChange={(e) => setLooks({ navigationCategory: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="gender">Gender</MenuItem>
                            <MenuItem value="type">Type</MenuItem>
                            <MenuItem value="season">Season</MenuItem>
                            <MenuItem value="collection">Collection</MenuItem>
                        </TextField>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_order', 'Display order')}
                    help={tr('cfg_stl_grid_order_help', 'How looks are sorted on the overview page')}
                    control={
                        <TextField
                            select size="small"
                            value={config.looksGrid.displayOrder}
                            onChange={(e) => setLooks({ displayOrder: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="default">{tr('cfg_stl_order_default', 'Default')}</MenuItem>
                            <MenuItem value="newest">{tr('cfg_stl_order_newest', 'Newest first')}</MenuItem>
                            <MenuItem value="alpha">{tr('cfg_stl_order_alpha', 'Alphabetical')}</MenuItem>
                            <MenuItem value="manual">{tr('cfg_stl_order_manual', 'Manual')}</MenuItem>
                        </TextField>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_looks_per_row', 'Looks per row')}
                    help={tr('cfg_stl_grid_looks_per_row_help', 'How many looks appear side by side in the grid')}
                    control={
                        <TextField
                            type="number" size="small"
                            value={config.looksGrid.looksPerRow}
                            onChange={(e) => setLooks({ looksPerRow: Math.max(1, parseInt(e.target.value) || 1) })}
                            InputProps={{ endAdornment: <InputAdornment position="end">{tr('cfg_stl_looks_unit', 'looks')}</InputAdornment> }}
                            sx={{ width: 130 }}
                        />
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_grid_iframe', 'Show in iframe')}
                    help={tr('cfg_stl_grid_iframe_help', 'Display the looks page inside an iframe. Recommended only when using advanced custom styling or scripts.')}
                    control={<Switch color="success" size="small" checked={config.looksGrid.showInIframe} onChange={(e) => setLooks({ showInIframe: e.target.checked })} />}
                />
            </ConfigCard>

            {/* ─── Look card overlay ─── */}
            <ConfigCard title={tr('cfg_stl_overlay_title', 'Look card overlay')} description={tr('cfg_stl_overlay_desc', 'The colored band shown over each look card with the look name and a call-to-action button.')} theme={theme}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 280px' }, gap: 2 }}>
                    {/* Left: settings */}
                    <Stack spacing={1.5}>
                        <SettingRow
                            label={tr('cfg_stl_overlay_hover_only', 'Show overlay only on hover (desktop)')}
                            help={tr('cfg_stl_overlay_hover_only_help', "When on, the overlay band only appears when a visitor hovers a look. When off, it's always visible.")}
                            control={<Switch color="success" size="small" checked={config.overlay.onlyOnHover} onChange={(e) => setOverlay({ onlyOnHover: e.target.checked })} />}
                        />

                        {/* Band style block */}
                        <SubBlock label={tr('cfg_stl_overlay_band_style', 'Band style')} help={tr('cfg_stl_overlay_band_style_help', 'The colored strip across the look card')}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                <ColorTile label={tr('cfg_stl_overlay_band_bg', 'BACKGROUND')} value={config.overlay.bandBg}    onChange={(v) => setOverlay({ bandBg: v })} />
                                <PercentInput label={tr('cfg_stl_overlay_band_height', 'HEIGHT')} value={config.overlay.bandHeightPct} onChange={(v) => setOverlay({ bandHeightPct: v })} />
                                <SelectField  label={tr('cfg_stl_overlay_band_vpos',   'VERTICAL POSITION')} value={config.overlay.bandVerticalPosition} onChange={(v) => setOverlay({ bandVerticalPosition: v })}
                                    options={[
                                        { value: 'top',    label: tr('cfg_stl_overlay_pos_top',    'Top')    },
                                        { value: 'middle', label: tr('cfg_stl_overlay_pos_middle', 'Middle') },
                                        { value: 'bottom', label: tr('cfg_stl_overlay_pos_bottom', 'Bottom') },
                                    ]}
                                />
                            </Box>
                        </SubBlock>

                        <SettingRow
                            label={tr('cfg_stl_overlay_show_title', 'Show look title on the band')}
                            help={tr('cfg_stl_overlay_show_title_help', "Display the look's name (e.g. \"Autumn casual\") on the colored band")}
                            control={<Switch color="success" size="small" checked={config.overlay.showTitle} onChange={(e) => setOverlay({ showTitle: e.target.checked })} />}
                        />
                        {config.overlay.showTitle && (
                            <SubBlock label={tr('cfg_stl_overlay_title_style', 'Title style')}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                    <ColorTile label={tr('cfg_stl_overlay_title_color', 'TEXT COLOR')} value={config.overlay.titleColor} onChange={(v) => setOverlay({ titleColor: v })} />
                                    <PixelInput label={tr('cfg_stl_overlay_title_size', 'FONT SIZE')} value={config.overlay.titleFontSize} onChange={(v) => setOverlay({ titleFontSize: v })} />
                                </Box>
                            </SubBlock>
                        )}

                        <SettingRow
                            label={tr('cfg_stl_overlay_show_cta', 'Show call-to-action button')}
                            help={tr('cfg_stl_overlay_show_cta_help', 'A button visitors click to open the look detail page')}
                            control={<Switch color="success" size="small" checked={config.overlay.showCta} onChange={(e) => setOverlay({ showCta: e.target.checked })} />}
                        />
                        {config.overlay.showCta && (
                            <>
                                <SubBlock label={tr('cfg_stl_overlay_button_text', 'Button text')} help={tr('cfg_stl_overlay_button_text_help', "The label shown on the button (e.g. \"Bewonder\", \"View look\")")}>
                                    <TextField
                                        size="small" fullWidth
                                        value={config.overlay.ctaText}
                                        onChange={(e) => setOverlay({ ctaText: e.target.value })}
                                    />
                                </SubBlock>
                                <SubBlock label={tr('cfg_stl_overlay_button_style', 'Button style')}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', gap: 1.5, alignItems: 'flex-end' }}>
                                        <ColorTile label={tr('cfg_stl_overlay_band_bg', 'BACKGROUND')} value={config.overlay.ctaBg}        onChange={(v) => setOverlay({ ctaBg: v })} />
                                        <ColorTile label={tr('cfg_stl_overlay_title_color', 'TEXT COLOR')} value={config.overlay.ctaTextColor} onChange={(v) => setOverlay({ ctaTextColor: v })} />
                                        <PixelInput label={tr('cfg_stl_overlay_title_size', 'FONT SIZE')} value={config.overlay.ctaFontSize} onChange={(v) => setOverlay({ ctaFontSize: v })} />
                                    </Box>
                                </SubBlock>
                            </>
                        )}
                    </Stack>

                    {/* Right: live preview tile */}
                    <Box>
                        <Paper variant="outlined" sx={{
                            position: 'relative',
                            aspectRatio: '3 / 4',
                            backgroundColor: '#f5e8d8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden',
                            borderRadius: 1.5,
                        }}>
                            <Typography variant="caption" sx={{ position: 'absolute', top: 6, left: 8, fontSize: '0.6rem', color: 'text.secondary', letterSpacing: 0.5 }}>
                                {tr('cfg_stl_overlay_live_preview', 'LIVE PREVIEW')}
                            </Typography>
                            {/* Person silhouette */}
                            <Box sx={{
                                position: 'absolute', left: '50%', top: '12%', bottom: '5%',
                                width: '55%', transform: 'translateX(-50%)',
                                opacity: 0.5,
                            }}>
                                <Box sx={{ width: 50, height: 50, borderRadius: '50%', border: '1.5px solid #999', mx: 'auto', mt: 0 }} />
                                <Box sx={{ width: '90%', height: '70%', borderLeft: '1.5px solid #999', borderRight: '1.5px solid #999', mx: 'auto', mt: -0.5 }} />
                            </Box>
                            {/* Band overlay */}
                            <Box sx={{
                                position: 'absolute',
                                left: 0, right: 0,
                                top:    config.overlay.bandVerticalPosition === 'top'    ? 0   : config.overlay.bandVerticalPosition === 'middle' ? '50%' : 'auto',
                                bottom: config.overlay.bandVerticalPosition === 'bottom' ? 0   : 'auto',
                                transform: config.overlay.bandVerticalPosition === 'middle' ? 'translateY(-50%)' : 'none',
                                height: `${Math.max(8, Math.min(80, Number(config.overlay.bandHeightPct) || 0))}%`,
                                backgroundColor: config.overlay.bandBg,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                                opacity: config.overlay.onlyOnHover ? 0.7 : 1,
                            }}>
                                {config.overlay.showTitle && (
                                    <Typography sx={{
                                        color: config.overlay.titleColor,
                                        fontSize: `${Math.min(20, Number(config.overlay.titleFontSize) || 12)}px`,
                                        fontWeight: 600,
                                    }}>
                                        Autumn casual
                                    </Typography>
                                )}
                                {config.overlay.showCta && (
                                    <Box sx={{
                                        backgroundColor: config.overlay.ctaBg,
                                        color: config.overlay.ctaTextColor,
                                        px: 1.5, py: 0.5,
                                        borderRadius: 99,
                                        fontSize: `${Math.min(14, Math.max(10, Number(config.overlay.ctaFontSize) / 2)) }px`,
                                        fontWeight: 500,
                                    }}>
                                        {config.overlay.ctaText || 'Bewonder'}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, textAlign: 'center' }}>
                            {tr('cfg_stl_overlay_preview_caption', 'Preview updates as you change the settings on the left.')}
                        </Typography>
                    </Box>
                </Box>
            </ConfigCard>

            {/* ─── Look detail page ─── */}
            <ConfigCard title={tr('cfg_stl_detail_title', 'Look detail page')} description={tr('cfg_stl_detail_desc', 'The page a visitor sees when they open a single look.')} theme={theme}>
                <SettingRow
                    label={tr('cfg_stl_detail_desc_pos', 'Description position')}
                    help={tr('cfg_stl_detail_desc_pos_help', "Where the look's description text sits on the detail page")}
                    control={
                        <ToggleButtonGroup
                            exclusive size="small"
                            value={config.detailPage.descriptionPosition}
                            onChange={(_e, v) => v && setDetail({ descriptionPosition: v })}
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', flexDirection: 'column', gap: 0.5, px: 2, py: 1, minWidth: 130 } }}
                        >
                            <ToggleButton value="below">
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                                    <Box sx={{ width: 30, height: 18, backgroundColor: 'currentColor', opacity: 0.7, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 30, height: 4,  backgroundColor: 'currentColor', opacity: 0.4, borderRadius: 0.5 }} />
                                </Box>
                                <Typography variant="caption">{tr('cfg_stl_detail_pos_below', 'Below the main photo')}</Typography>
                            </ToggleButton>
                            <ToggleButton value="top">
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'center' }}>
                                    <Box sx={{ width: 30, height: 4,  backgroundColor: 'currentColor', opacity: 0.4, borderRadius: 0.5 }} />
                                    <Box sx={{ width: 30, height: 18, backgroundColor: 'currentColor', opacity: 0.7, borderRadius: 0.5 }} />
                                </Box>
                                <Typography variant="caption">{tr('cfg_stl_detail_pos_top', 'Top, centered')}</Typography>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    }
                />
                <SettingRow
                    label={tr('cfg_stl_detail_show_divider', 'Show divider between products in the look')}
                    help={tr('cfg_stl_detail_show_divider_help', 'A thin separator line between each product shown on the look detail page')}
                    control={<Switch color="success" size="small" checked={config.detailPage.showDividerBetweenProducts} onChange={(e) => setDetail({ showDividerBetweenProducts: e.target.checked })} />}
                />
                <SettingRow
                    label={tr('cfg_stl_detail_show_oos', 'Show out-of-stock products')}
                    help={tr('cfg_stl_detail_show_oos_help', "Keep products in the look's product list even if they are no longer available")}
                    control={<Switch color="success" size="small" checked={config.detailPage.showOutOfStock} onChange={(e) => setDetail({ showOutOfStock: e.target.checked })} />}
                />
            </ConfigCard>

            {/* ─── Product title & subtitle (look detail page) ─── */}
            <ConfigCard title={tr('cfg_stl_pts_title', 'Product title & subtitle (look detail page)')} description={tr('cfg_stl_pts_desc', 'How each product within a look is identified. Drag chips to reorder, or click an available field to add it.')} theme={theme}>
                <ChipRow
                    label={tr('cfg_stl_pts_title_lr', 'TITLE (LEFT TO RIGHT)')}
                    chips={config.productTitle}
                    onRemove={removeFrom('productTitle')}
                    placeholder={tr('cfg_stl_pts_title_placeholder', 'Drag fields here')}
                    tr={tr}
                />
                <ChipRow
                    label={tr('cfg_stl_pts_subtitle_lr', 'SUBTITLE (LEFT TO RIGHT)')}
                    chips={config.productSubtitle}
                    onRemove={removeFrom('productSubtitle')}
                    placeholder={tr('cfg_stl_pts_subtitle_placeholder', 'Drag fields here')}
                    tr={tr}
                />
                <Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, letterSpacing: 0.5 }}>
                        {tr('cfg_stl_pts_available', 'AVAILABLE FIELDS (CLICK OR DRAG TO ADD)')}
                    </Typography>
                    {availableFields.length === 0 ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            {tr('cfg_stl_pts_all_used', 'All fields are in use.')}
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {availableFields.map((f) => (
                                <Box key={f.key} sx={{ display: 'flex', gap: 0.25 }}>
                                    <Button
                                        size="small" variant="outlined"
                                        startIcon={<AddIcon fontSize="small" />}
                                        onClick={() => addToTitle(f.key)}
                                        sx={{ textTransform: 'none', borderRadius: 1 }}
                                    >
                                        {f.label}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </ConfigCard>
        </Stack>
    );
};

// ─────────── helpers ───────────

const SettingRow = ({ label, help, control }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
        </Box>
        <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
);

const SubBlock = ({ label, help, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
        <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', display: 'block' }}>{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{help}</Typography>}
        </Box>
        {children}
    </Paper>
);

const ColorTile = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <Box component="label" sx={{
            position: 'relative', display: 'block',
            width: 36, height: 36, borderRadius: 1,
            border: '1px solid rgba(0,0,0,0.18)',
            backgroundColor: value, cursor: 'pointer',
        }}>
            <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
        </Box>
    </Box>
);
const PercentInput = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            type="number" size="small" fullWidth
            value={value}
            onChange={(e) => onChange(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        />
    </Box>
);
const PixelInput = ({ label, value, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            type="number" size="small" fullWidth
            value={value}
            onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
        />
    </Box>
);
const SelectField = ({ label, value, onChange, options }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontSize: '0.65rem', letterSpacing: 0.5 }}>{label}</Typography>
        <TextField
            select size="small" fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
        >
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
    </Box>
);

const ChipRow = ({ label, chips, onRemove, placeholder, tr }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, letterSpacing: 0.5 }}>{label}</Typography>
        <Paper variant="outlined" sx={{
            minHeight: 44, p: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', borderRadius: 1,
            backgroundColor: chips.length === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
        }}>
            {chips.length === 0 ? (
                <Typography variant="caption" sx={{ color: 'text.disabled', pl: 0.75 }}>{placeholder}</Typography>
            ) : (
                chips.map((key) => {
                    const def = CHIP_FIELDS.find(f => f.key === key);
                    const label = def ? def.label : key;
                    return (
                        <Chip
                            key={key}
                            label={label}
                            onDelete={() => onRemove(key)}
                            size="small"
                            icon={<DragIndicatorIcon fontSize="small" sx={{ ml: '4px !important', color: 'text.secondary' }} />}
                            sx={{
                                backgroundColor: '#dbeafe',
                                color: '#1e3a8a',
                                fontWeight: 500,
                                '& .MuiChip-deleteIcon': { color: '#1e3a8a' },
                                '& .MuiChip-icon': { color: '#1e3a8a' },
                            }}
                        />
                    );
                })
            )}
        </Paper>
    </Box>
);

ShopTheLookTab.propTypes = { theme: PropTypes.string, t: PropTypes.func };
export default ShopTheLookTab;
